import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { DocTypeSidebar } from "@/components/DocTypeSidebar";
import { GeneratorForm } from "@/components/GeneratorForm";
import { DocumentViewer } from "@/components/DocumentViewer";
import { DOC_TYPES } from "@/lib/docTypes";
import { cleanMath } from "@/lib/mathText";
import { ShieldAlert, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getToken = () => sessionStorage.getItem("pk_token");

function App() {
  const [config, setConfig] = useState(null);
  const [docType, setDocType] = useState("priprema");
  const [content, setContent] = useState("");
  const [docTitle, setDocTitle] = useState("Dokument");
  const [viewerDocType, setViewerDocType] = useState("priprema");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [authStatus, setAuthStatus] = useState(null); // null = provjera, "ok", "denied"
  const [authError, setAuthError] = useState("");
  const viewerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const body = {
      uid: params.get("uid") || "",
      email: params.get("email") || "",
      exp: params.get("exp") || "",
      sig: params.get("sig") || "",
    };
    axios
      .post(`${API}/auth/verify`, body)
      .then(({ data }) => {
        if (data && data.token) {
          sessionStorage.setItem("pk_token", data.token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        }
        setAuthStatus("ok");
      })
      .catch((e) => {
        setAuthError(
          e?.response?.data?.detail ||
            "Pristup ovoj aplikaciji moguć je samo prijavom preko Zbornice."
        );
        setAuthStatus("denied");
      });
  }, []);

  useEffect(() => {
    if (authStatus !== "ok") return;
    axios.get(`${API}/config`).then((res) => setConfig(res.data)).catch(() => {});
  }, [authStatus]);

  const handleGenerate = async (payload) => {
    setGenerating(true);
    setContent("");
    setError("");
    setViewerDocType(payload.doc_type);
    const dt = DOC_TYPES.find((d) => d.id === payload.doc_type);
    let titleParts;
    if (payload.doc_type === "clanak") {
      titleParts = ["Članak za web"];
    } else if (payload.doc_type === "vlastiti") {
      titleParts = [payload.custom_title];
    } else {
      const detail =
        payload.lesson ||
        payload.activity_name ||
        payload.destination ||
        (payload.subject
          ? `${payload.subject} ${payload.grade}. razred`
          : payload.grade
          ? `${payload.grade}. razred`
          : "");
      titleParts = [dt.label, detail].filter(Boolean);
    }
    setDocTitle(titleParts.join(" - "));
    setTimeout(() => viewerRef.current?.scrollIntoView({ behavior: "smooth" }), 100);

    try {
      const res = await fetch(`${API}/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || "Generiranje nije uspjelo. Pokušajte ponovno.";
        toast.error(msg);
        setError(msg);
        setGenerating(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let data;
          try {
            data = JSON.parse(line.slice(6));
          } catch {
            continue;
          }
          if (data.delta) {
            acc += data.delta;
            setContent(cleanMath(acc));
          } else if (data.error) {
            toast.error(data.error);
            setError(data.error);
          }
        }
      }
    } catch {
      const msg = "Veza s poslužiteljem je prekinuta. Pokušajte ponovno.";
      toast.error(msg);
      setError(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleArticle = () => {
    handleGenerate({
      doc_type: "clanak",
      source_content: content,
      source_doc_type: viewerDocType,
      teacher_name: localStorage.getItem("pk_teacher") || "",
      school: localStorage.getItem("pk_school") || "",
    });
  };

  const handleReset = () => {
    setContent("");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (authStatus === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-500" data-testid="auth-checking">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm font-medium">Provjera pristupa...</p>
        </div>
      </div>
    );
  }

  if (authStatus === "denied") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div
          data-testid="auth-denied"
          className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-5">
            <ShieldAlert className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 mb-2">Pristup nije dopušten</h1>
          <p className="text-sm text-slate-600 leading-relaxed">{authError}</p>
          <p className="text-xs text-slate-400 mt-4">
            Otvorite Pripremko putem svoje Zbornice, prijavljeni svojim računom.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      <Header />

      <section className="bg-blue-50/70 border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 max-w-3xl">
            Pripreme, GIK-ovi i sva ostala dokumentacija - <span className="text-blue-600">gotovo u minuti</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Odaberite razred, predmet i lekciju, a umjetna inteligencija izradit će strukturirani
            dokument koji možete pregledati, urediti i preuzeti kao PDF. Jednostavno kao 1, 2, 3.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          <DocTypeSidebar docType={docType} onSelect={(id) => { setDocType(id); setContent(""); }} />
          <div className="flex-1 min-w-0 space-y-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-1">
                2. Ispunite podatke
              </p>
              <GeneratorForm
                key={docType}
                docType={docType}
                config={config}
                generating={generating}
                onGenerate={handleGenerate}
              />
            </div>
            <div ref={viewerRef}>
              {error && (
                <div
                  data-testid="error-banner"
                  className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-900 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-red-800 mb-1">Generiranje nije uspjelo</p>
                      <p className="whitespace-pre-wrap leading-relaxed">{error}</p>
                    </div>
                    <button
                      data-testid="close-error-btn"
                      onClick={() => setError("")}
                      className="text-red-700 hover:text-red-900 font-semibold text-lg leading-none"
                      aria-label="Zatvori poruku"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              {(content || generating) && (
                <>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-1">
                    3. Vaš dokument
                  </p>
                  <DocumentViewer
                    content={content}
                    title={docTitle}
                    generating={generating}
                    onReset={handleReset}
                    onContentChange={setContent}
                    docType={viewerDocType}
                    onGenerateArticle={handleArticle}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-sm text-slate-400 flex flex-wrap items-center justify-between gap-2">
          <span>Pripremko, pomoćnik za učitelje u Republici Hrvatskoj</span>
          <span>Generirane dokumente uvijek pregledajte prije službene uporabe.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
